import { useMemo } from "react"
import { Box, Card, Typography, useTheme, Stack } from "@mui/material"
import { Link } from "react-router-dom"

import { IHomeNavigationItem, homeNavigations } from "standard"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useHome } from "../../hooks/home/useHome"
import { useHomeMap } from "hooks/home/useHomeMap"

import { FullScreenOverlayLayout } from "layouts/FullScreenOverlayLayout"

import { AppNavigation as DefaultAppNavigation } from "containers/AppNavigation"

import { WelcomePanel } from "components/WelcomePanel"
import { Head } from "components/Head"
import { Carousel, CarouselItem } from "components/Carousel"
import { HighlightFeatureCard } from "components/domains/events"
import {
  FullScreenStart,
  StandardFullScreenStart,
} from "components/domains/home"
import { CalendarStarIcon, PercentIcon } from "components/icons"
import { useSponsoredHomepageSpotlight } from "hooks/sponsored-content/useSponsoredHomepageSpotlight"

export interface IHomePageProps {
  navigations?: IHomeNavigationItem[]
  slotProps?: {
    renderFullScreenStart?: React.ComponentType
  }
  AppNavigation?: React.ComponentType
  HomeHeaderComponent?: React.ComponentType<{
    navigations?: IHomeNavigationItem[]
  }>
}

export const HomePage = ({
  navigations = homeNavigations,
  slotProps,
  AppNavigation = DefaultAppNavigation,
  HomeHeaderComponent = WelcomePanel,
}: IHomePageProps) => {
  const { renderFullScreenStart: StartButton = FullScreenStart } =
    slotProps || {}
  const theme = useTheme()
  const { t } = useTranslation()
  const { homepageSpotlights } = useSponsoredHomepageSpotlight()
  const {
    featuredEventPromotion,
    defaultItemsLinks,
    venues,
    defaultRoutePath,
  } = useHome()
  useHomeMap(venues)

  const defaultLinkIcons = useMemo(() => {
    return {
      events: getIcon(theme?.assets?.defaultLinkIcon?.events) || (
        <CalendarStarIcon />
      ),
      promotions: getIcon(theme?.assets?.defaultLinkIcon?.promotions) || (
        <PercentIcon />
      ),
    }
  }, [theme])

  const {
    root,
    contentContainer,
    featureCarouselWrapper,
    welcomePanelWrapper,
    textCard,
    linkCardContainer,
    defaultCardPrimaryText,
    defaultCardSecondaryText,
    defaultCardIcon,
    navigationWrapper,
    carousel,
    carouselItem,
    defaultCardTextContainer,
  } = theme.components[`HomePage`]?.styleOverrides || {}

  const { homeCarouselOptions, startButtonvariant } =
    theme.components[`HomePage`]?.defaultProps || {}

  const StartButtonComponent = useMemo(
    () =>
      startButtonvariant === "standardV1"
        ? StandardFullScreenStart
        : StartButton,
    [startButtonvariant, StartButton]
  )

  return (
    <>
      <Head title={t("Indoor map")} />
      <FullScreenOverlayLayout sx={root}>
        <Box sx={contentContainer}>
          <StartButtonComponent path={defaultRoutePath} />
          <Box sx={welcomePanelWrapper}>
            <HomeHeaderComponent navigations={navigations} />
          </Box>
        </Box>
        <Box sx={featureCarouselWrapper}>
          <Carousel
            options={{
              gap: theme.spacing(1.25),
              pagination: false,
              focus: "center",
              arrows: false,
              mediaQuery: "min",
              breakpoints: {
                640: {
                  perPage: 2,
                },
                900: {
                  perPage: 3,
                },
              },
              ...homeCarouselOptions,
            }}
            sx={carousel}
          >
            {[...featuredEventPromotion, ...homepageSpotlights].map(
              (feature) => {
                const { feature_type, id } = feature
                return (
                  <CarouselItem key={id} sx={carouselItem}>
                    {/*
                     * @Example: Event => events/event-id, Promotion => promotions/promotion-id
                     */}
                    <Link to={`${feature_type}s/${id}`}>
                      <HighlightFeatureCard feature={feature} />
                    </Link>
                  </CarouselItem>
                )
              }
            )}
            {defaultItemsLinks && defaultItemsLinks.length > 0 ? (
              <CarouselItem>
                <Box sx={linkCardContainer}>
                  {defaultItemsLinks.map(
                    ({ to, primaryText, secondaryText, icon }) => (
                      <Link key={to} to={to}>
                        <Card sx={textCard}>
                          <Stack spacing={1}>
                            <Box sx={defaultCardIcon}>
                              {defaultLinkIcons[icon]}
                            </Box>
                            <Stack sx={defaultCardTextContainer}>
                              <Typography
                                variant="title"
                                sx={defaultCardPrimaryText}
                              >
                                {t(primaryText)}
                              </Typography>
                              <Typography
                                variant="subTitle"
                                sx={defaultCardSecondaryText}
                              >
                                {t(secondaryText)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Card>
                      </Link>
                    )
                  )}
                </Box>
              </CarouselItem>
            ) : null}
          </Carousel>
        </Box>
        <Box sx={navigationWrapper}>
          <AppNavigation />
        </Box>
      </FullScreenOverlayLayout>
    </>
  )
}
